"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Pattern and walker for RNG's ``choice`` elements.
 * @author Louis-Dominique Dubeau
 * @license MPL 2.0
 * @copyright Mangalam Research Center for Buddhist Languages
 */
const errors_1 = require("../errors");
const set_1 = require("../set");
const base_1 = require("./base");
const empty_1 = require("./empty");
/**
 * A pattern for ``<choice>``.
 */
class Choice extends base_1.TwoSubpatterns {
    constructor(xmlPath, patA, patB) {
        super(xmlPath, patA, patB);
        this.optional = patA instanceof empty_1.Empty;
    }
    _computeHasEmptyPattern() {
        return this.patA.hasEmptyPattern() || this.patB.hasEmptyPattern();
    }
    newWalker() {
        const hasAttrs = this.hasAttrs();
        const walkerB = this.patB.newWalker();
        if (this.optional) {
            // tslint:disable-next-line:no-use-before-declare
            return new OptionalChoiceWalker(this, walkerB, hasAttrs, false, true, true);
        }
        else {
            const walkerA = this.patA.newWalker();
            // tslint:disable-next-line:no-use-before-declare
            return new ChoiceWalker(this, walkerA, walkerB, hasAttrs, false, false, !hasAttrs || walkerA.canEndAttribute ||
                walkerB.canEndAttribute, walkerA.canEnd || walkerB.canEnd);
        }
    }
}
exports.Choice = Choice;
/**
 * Walker for [[Choice]].
 */
class ChoiceWalker {
    constructor(el, walkerA, walkerB, hasAttrs, deactivateA, deactivateB, canEndAttribute, canEnd) {
        this.el = el;
        this.walkerA = walkerA;
        this.walkerB = walkerB;
        this.hasAttrs = hasAttrs;
        this.deactivateA = deactivateA;
        this.deactivateB = deactivateB;
        this.canEndAttribute = canEndAttribute;
        this.canEnd = canEnd;
    }
    clone() {
        return new ChoiceWalker(this.el, this.walkerA.clone(), this.walkerB.clone(), this.hasAttrs, this.deactivateA, this.deactivateB, this.canEndAttribute, this.canEnd);
    }
    possible() {
        const walkerA = this.walkerA;
        let ret = this.deactivateA ? undefined : walkerA.possible();
        const walkerB = this.walkerB;
        if (!this.deactivateB) {
            const possibleB = walkerB.possible();
            if (ret === undefined) {
                ret = possibleB;
            }
            else {
                set_1.union(ret, possibleB);
            }
        }
        else if (ret === undefined) {
            ret = new Set();
        }
        return ret;
    }
    possibleAttributes() {
        const walkerA = this.walkerA;
        let ret = this.deactivateA ? undefined : walkerA.possibleAttributes();
        const walkerB = this.walkerB;
        if (!this.deactivateB) {
            const possibleB = walkerB.possibleAttributes();
            if (ret === undefined) {
                ret = possibleB;
            }
            else {
                set_1.union(ret, possibleB);
            }
        }
        else if (ret === undefined) {
            ret = new Set();
        }
        return ret;
    }
    fireEvent(name, params, nameResolver) {
        if (this.deactivateA && this.deactivateB) {
            return new base_1.InternalFireEventResult(false);
        }
        const evIsAttributeEvent = base_1.isAttributeEvent(name);
        if (evIsAttributeEvent && !this.hasAttrs) {
            return new base_1.InternalFireEventResult(false);
        }
        const retA = this.deactivateA ? new base_1.InternalFireEventResult(false) :
            this.walkerA.fireEvent(name, params, nameResolver);
        const retB = this.deactivateB ? new base_1.InternalFireEventResult(false) :
            this.walkerB.fireEvent(name, params, nameResolver);
        if (retA.matched) {
            if (!retB.matched) {
                this.deactivateB = true;
                if (evIsAttributeEvent) {
                    this.canEndAttribute = this.walkerA.canEndAttribute;
                }
                this.canEnd = this.walkerA.canEnd;
            }
            else {
                if (evIsAttributeEvent) {
                    this.canEndAttribute = this.walkerA.canEndAttribute ||
                        this.walkerB.canEndAttribute;
                }
                this.canEnd = this.walkerA.canEnd || this.walkerB.canEnd;
            }
            return retA.combine(retB);
        }
        if (retB.matched) {
            this.deactivateA = true;
            if (evIsAttributeEvent) {
                this.canEndAttribute = this.walkerB.canEndAttribute;
            }
            this.canEnd = this.walkerB.canEnd;
        }
        return retB.combine(retA);
    }
    end() {
        if (this.canEnd) {
            // Instead of an ended flag, we set both flags.
            this.deactivateA = true;
            this.deactivateB = true;
            return false;
        }
        const retA = this.deactivateA ? false : this.walkerA.end();
        const retB = this.deactivateB ? false : this.walkerB.end();
        if (!retA) {
            return retB;
        }
        if (!retB) {
            return retA;
        }
        // If we are here both walkers exist and returned an error. We combine the
        // errors no matter which walker may have been deactivated.
        const combined = this.combineChoices();
        return combined.length !== 0 ? combined : retA;
    }
    endAttributes() {
        if (this.canEndAttribute) {
            return false;
        }
        const retA = this.deactivateA ? false : this.walkerA.endAttributes();
        const retB = this.deactivateB ? false : this.walkerB.endAttributes();
        if (!retA) {
            return retB;
        }
        if (!retB) {
            return retA;
        }
        // If we are here both walkers exist and returned an error. We combine the
        // errors no matter which walker may have been deactivated.
        const combined = this.combineChoices();
        return combined.length !== 0 ? combined : retA;
    }
    combineChoices() {
        const namesA = [];
        const values = [];
        let notAChoiceError = false;
        for (const ev of this.walkerA.possible()) {
            const name = ev.params[0];
            if (name === "enterStartTag" || name === "attributeName") {
                namesA.push(ev.params[1]);
            }
            else if (name === "attributeValue" || name === "text") {
                values.push(ev.params[1]);
            }
            else {
                notAChoiceError = true;
                break;
            }
        }
        if (!notAChoiceError) {
            const namesB = [];
            for (const ev of this.walkerB.possible()) {
                const name = ev.params[0];
                if (name === "enterStartTag" || name === "attributeName") {
                    namesB.push(ev.params[1]);
                }
                else if (name === "attributeValue" || name === "text") {
                    values.push(ev.params[1]);
                }
                else {
                    notAChoiceError = true;
                    break;
                }
            }
            if (!notAChoiceError) {
                return [
                    values.length !== 0 ?
                        new errors_1.ValidationError(`one value required from the following: ${values.join(", ")}`) :
                        new errors_1.ChoiceError(namesA, namesB),
                ];
            }
        }
        // We cannot make a good combination.
        return [];
    }
}
/**
 * Walker for [[Choice]].
 */
class OptionalChoiceWalker {
    constructor(el, walkerB, hasAttrs, ended, canEndAttribute, canEnd) {
        this.el = el;
        this.walkerB = walkerB;
        this.hasAttrs = hasAttrs;
        this.ended = ended;
        this.canEndAttribute = canEndAttribute;
        this.canEnd = canEnd;
    }
    clone() {
        return new OptionalChoiceWalker(this.el, this.walkerB.clone(), this.hasAttrs, this.ended, this.canEndAttribute, this.canEnd);
    }
    possible() {
        return this.ended ? new Set() : this.walkerB.possible();
    }
    possibleAttributes() {
        return this.ended ? new Set() : this.walkerB.possibleAttributes();
    }
    fireEvent(name, params, nameResolver) {
        if (this.ended) {
            return new base_1.InternalFireEventResult(false);
        }
        const evIsAttributeEvent = base_1.isAttributeEvent(name);
        if (evIsAttributeEvent && !this.hasAttrs) {
            return new base_1.InternalFireEventResult(false);
        }
        if (name === "text" && !/\S/.test(params[0])) {
            return new base_1.InternalFireEventResult(true);
        }
        const retB = this.walkerB.fireEvent(name, params, nameResolver);
        if (retB.matched) {
            if (evIsAttributeEvent) {
                this.canEndAttribute = this.walkerB.canEndAttribute;
            }
            this.canEnd = this.walkerB.canEnd;
        }
        return retB;
    }
    end() {
        if (this.canEnd) {
            this.ended = true;
            return false;
        }
        return this.walkerB.end();
    }
    endAttributes() {
        if (this.canEndAttribute) {
            return false;
        }
        return this.walkerB.endAttributes();
    }
}
//  LocalWords:  RNG's MPL retA ChoiceWalker enterStartTag notAChoiceError
//  LocalWords:  tslint ChoiceError
//# sourceMappingURL=choice.js.map